import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getLessonById, markLessonComplete, submitLessonTask } from "@/lib/academy.functions";

export const Route = createFileRoute("/aluno/aula/$id")({
  ssr: false,
  component: LessonView,
});

function toEmbed(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch {
    return null;
  }
}

function LessonView() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["academy", "lesson", id],
    queryFn: () => getLessonById({ data: { id } }),
    staleTime: 30_000,
  });

  const complete = useMutation({
    mutationFn: () => markLessonComplete({ data: { lessonId: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["academy"] }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Carregando aula…</p>;
  if (error || !data) return <p className="text-sm text-destructive">Aula não encontrada.</p>;

  const { lesson, module, course, tasks, submissions, progress } = data;
  const isCompleted = Boolean(progress?.completed_at);
  const embed = toEmbed(lesson.video_url);

  return (
    <div className="space-y-6">
      <div className="text-xs text-muted-foreground">
        {course && (
          <Link to="/aluno/curso/$slug" params={{ slug: course.slug }} className="hover:text-foreground">
            ← {course.title}
          </Link>
        )}
        {module && <span> · {module.title}</span>}
      </div>

      <h1 className="text-3xl font-bold tracking-tight">{lesson.title}</h1>

      {embed && (
        <div className="aspect-video overflow-hidden rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-black">
          <iframe src={embed} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      )}

      {lesson.pdf_url && (
        <a href={lesson.pdf_url} target="_blank" rel="noopener" className="btn-outline inline-flex">
          📄 Baixar PDF de apoio
        </a>
      )}

      {lesson.content_md && (
        <article className="card-glass whitespace-pre-wrap text-sm leading-relaxed">{lesson.content_md}</article>
      )}

      {Array.isArray(lesson.tools) && lesson.tools.length > 0 && (
        <section className="card-glass">
          <h2 className="text-lg font-semibold">🛠 Ferramentas & links desta aula</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Abra em uma nova aba e siga junto com a aula.
          </p>
          <ul className="mt-4 grid gap-3 md:grid-cols-2">
            {lesson.tools.map((t: any, i: number) => (
              <li key={i} className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-3">
                <a
                  href={t.url}
                  target="_blank"
                  rel="noopener"
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  🔗 {t.name}
                </a>
                {t.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{t.description}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => complete.mutate()}
          disabled={isCompleted || complete.isPending}
          className="btn-primary disabled:opacity-60"
        >
          {isCompleted ? "✓ Aula concluída" : complete.isPending ? "Salvando…" : `Marcar como concluída (+${lesson.points || 0} pts)`}
        </button>
        {course && (
          <button onClick={() => navigate({ to: "/aluno/curso/$slug", params: { slug: course.slug } })} className="btn-outline">
            Voltar ao curso
          </button>
        )}
      </div>

      {tasks.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Tarefas práticas</h2>
          {tasks.map((t: any) => (
            <TaskCard key={t.id} task={t} submission={submissions.find((s: any) => s.task_id === t.id)} />
          ))}
        </section>
      )}
    </div>
  );
}

function TaskCard({ task, submission }: { task: any; submission: any | undefined }) {
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const submit = useMutation({
    mutationFn: () =>
      submitLessonTask({
        data: {
          taskId: task.id,
          content: content || undefined,
          link_url: linkUrl || undefined,
        },
      }),
    onSuccess: () => {
      setContent("");
      setLinkUrl("");
      qc.invalidateQueries({ queryKey: ["academy", "lesson"] });
    },
  });

  return (
    <div className="card-glass">
      <p className="text-sm">{task.prompt}</p>
      <div className="mt-1 text-xs text-muted-foreground">
        Tipo: {task.submission_type} {task.required ? "· obrigatória" : "· opcional"}
      </div>
      {submission ? (
        <div className="mt-3 rounded-lg border border-ink/10 bg-ink/[0.045] p-3 text-xs">
          <div>
            <span className="text-muted-foreground">Status:</span> <strong>{submission.status}</strong>
          </div>
          {submission.feedback && (
            <div className="mt-1">
              <span className="text-muted-foreground">Feedback:</span> {submission.feedback}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {(task.submission_type === "text" || task.submission_type === "mixed") && (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              placeholder="Descreva o que você fez…"
              className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm"
            />
          )}
          {(task.submission_type === "link" || task.submission_type === "mixed") && (
            <input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Link (Ficha, post, print…)"
              className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm"
            />
          )}
          <button onClick={() => submit.mutate()} disabled={submit.isPending} className="btn-primary text-sm">
            {submit.isPending ? "Enviando…" : "Enviar entrega"}
          </button>
          {submit.error && <p className="text-xs text-destructive">{(submit.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}